import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { format } from 'date-fns';
import { CalendarIcon, MapPin, Users, Clock, Car, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { cn } from '../lib/utils';
import { trackBookingConversion } from './TrackingScripts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_PLACES_API_KEY = process.env.REACT_APP_GOOGLE_PLACES_API_KEY;

// Victoria bounds for restricting autocomplete
const VICTORIA_BOUNDS = {
  north: -33.9806,
  south: -39.1592,
  east: 150.0209,
  west: 140.9617,
};

export const BookingForm = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    pickup_address: '',
    dropoff_address: '',
    fleet_type: '',
    passengers: '',
    pickup_date: null,
    pickup_time: '',
    driver_note: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const pickupInputRef = useRef(null);
  const dropoffInputRef = useRef(null);
  const pickupAutocompleteRef = useRef(null);
  const dropoffAutocompleteRef = useRef(null);

  // Initialize Google Places Autocomplete
  const initAutocomplete = useCallback(() => {
    if (!window.google || !window.google.maps || !window.google.maps.places) {
      return;
    }

    const options = {
      bounds: new window.google.maps.LatLngBounds(
        new window.google.maps.LatLng(VICTORIA_BOUNDS.south, VICTORIA_BOUNDS.west),
        new window.google.maps.LatLng(VICTORIA_BOUNDS.north, VICTORIA_BOUNDS.east)
      ),
      componentRestrictions: { country: 'au' },
      strictBounds: true,
      types: ['geocode', 'establishment'],
    };

    if (pickupInputRef.current && !pickupAutocompleteRef.current) {
      pickupAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        pickupInputRef.current,
        options
      );
      pickupAutocompleteRef.current.addListener('place_changed', () => {
        const place = pickupAutocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, pickup_address: place.formatted_address }));
        } else if (place.name) {
          setFormData(prev => ({ ...prev, pickup_address: place.name }));
        }
      });
    }

    if (dropoffInputRef.current && !dropoffAutocompleteRef.current) {
      dropoffAutocompleteRef.current = new window.google.maps.places.Autocomplete(
        dropoffInputRef.current,
        options
      );
      dropoffAutocompleteRef.current.addListener('place_changed', () => {
        const place = dropoffAutocompleteRef.current.getPlace();
        if (place.formatted_address) {
          setFormData(prev => ({ ...prev, dropoff_address: place.formatted_address }));
        } else if (place.name) {
          setFormData(prev => ({ ...prev, dropoff_address: place.name }));
        }
      });
    }
  }, []);

  useEffect(() => {
    // Check if Google Maps is already loaded (by Emergent or elsewhere)
    const checkAndInitAutocomplete = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return true;
      }
      return false;
    };

    // If already loaded, just init
    if (checkAndInitAutocomplete()) {
      return;
    }

    // If not loaded, load it
    const loadGoogleMaps = () => {
      if (!window.google && GOOGLE_PLACES_API_KEY) {
        // Check if script is already being loaded
        const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
        if (existingScript) {
          // Wait for existing script to load
          existingScript.addEventListener('load', () => {
            setTimeout(checkAndInitAutocomplete, 100);
          });
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_PLACES_API_KEY}&libraries=places&loading=async`;
        script.async = true;
        script.defer = true;
        
        script.onload = () => {
          setTimeout(checkAndInitAutocomplete, 100);
        };
        
        script.onerror = () => {
          console.error('Failed to load Google Places API');
        };
        
        document.head.appendChild(script);
      }
    };

    loadGoogleMaps();

    // Retry initialization periodically in case script loads async
    const retryInterval = setInterval(() => {
      if (checkAndInitAutocomplete()) {
        clearInterval(retryInterval);
      }
    }, 500);

    // Clear interval after 10 seconds
    setTimeout(() => clearInterval(retryInterval), 10000);

    // Fix for mobile touch events on Google Places autocomplete
    const fixTouchEvents = () => {
      const pacContainers = document.querySelectorAll('.pac-container');
      pacContainers.forEach(container => {
        // Make sure container is touchable
        container.style.pointerEvents = 'auto';
        container.style.touchAction = 'auto';
        
        // Add touch event listeners
        container.addEventListener('touchstart', (e) => {
          e.stopPropagation();
        }, { passive: true });
        
        container.addEventListener('touchend', (e) => {
          e.stopPropagation();
        }, { passive: true });
        
        // Ensure pac-items are touchable
        const items = container.querySelectorAll('.pac-item');
        items.forEach(item => {
          item.style.pointerEvents = 'auto';
          item.style.touchAction = 'auto';
        });
      });
    };
    
    // Observe for pac-container being added to DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          fixTouchEvents();
        }
      });
    });
    
    observer.observe(document.body, { childList: true, subtree: true });
    
    return () => {
      observer.disconnect();
      clearInterval(retryInterval);
    };
  }, [initAutocomplete]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Ref to control date popover
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);
  const timeSelectRef = useRef(null);

  const handleDateChange = (date) => {
    setFormData(prev => ({ ...prev, pickup_date: date, pickup_time: '' }));
    // Close the calendar popover
    setDatePopoverOpen(false);
    // Auto-focus time select after a short delay
    setTimeout(() => {
      if (timeSelectRef.current) {
        timeSelectRef.current.click();
      }
    }, 100);
  };

  const generateTimeSlots = () => {
    const slots = [];
    const now = new Date();
    const isToday = formData.pickup_date && 
      formData.pickup_date.toDateString() === now.toDateString();
    
    // Add "Now" option for today's bookings
    if (isToday) {
      slots.push({ value: 'NOW', label: '🚖 Now (ASAP)' });
    }
    
    // 5-minute intervals
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 5) {
        // If today is selected, only show times after current time + 5 min buffer
        if (isToday) {
          const slotTime = new Date();
          slotTime.setHours(h, m, 0, 0);
          const bufferTime = new Date(now.getTime() + 5 * 60 * 1000); // 5 min buffer
          if (slotTime <= bufferTime) {
            continue; // Skip past times
          }
        }
        
        const hour = h.toString().padStart(2, '0');
        const minute = m.toString().padStart(2, '0');
        const timeValue = `${hour}:${minute}`;
        
        // Format for display (12-hour format)
        const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h >= 12 ? 'PM' : 'AM';
        const displayTime = `${displayHour}:${minute.padStart(2, '0')} ${ampm}`;
        
        slots.push({ value: timeValue, label: displayTime });
      }
    }
    
    // If today and it's very late (no slots available), add next day's early morning slots
    if (isToday && slots.length <= 1) {
      // Add midnight to early morning as "tomorrow" options
      for (let h = 0; h < 6; h++) {
        for (let m = 0; m < 60; m += 5) {
          const hour = h.toString().padStart(2, '0');
          const minute = m.toString().padStart(2, '0');
          const timeValue = `${hour}:${minute}`;
          const displayHour = h === 0 ? 12 : h;
          const ampm = 'AM';
          const displayTime = `${displayHour}:${minute.padStart(2, '0')} ${ampm} (Tomorrow)`;
          slots.push({ value: timeValue, label: displayTime, isTomorrow: true });
        }
      }
    }
    
    return slots;
  };

  // Retry function with exponential backoff
  const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
    let lastError;
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await fn();
      } catch (err) {
        lastError = err;
        // Don't retry on validation errors (4xx)
        if (err.response && err.response.status >= 400 && err.response.status < 500) {
          throw err;
        }
        // Wait with exponential backoff before retrying
        if (attempt < maxRetries - 1) {
          const delay = baseDelay * Math.pow(2, attempt);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    throw lastError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Custom validation for select fields
    if (!formData.fleet_type) {
      setError('Please select a vehicle type');
      setIsSubmitting(false);
      return;
    }
    if (!formData.passengers) {
      setError('Please select number of passengers');
      setIsSubmitting(false);
      return;
    }
    if (!formData.pickup_date) {
      setError('Please select a pickup date');
      setIsSubmitting(false);
      return;
    }
    if (!formData.pickup_time) {
      setError('Please select a pickup time');
      setIsSubmitting(false);
      return;
    }

    try {
      // Handle "NOW" time option
      let pickupTime = formData.pickup_time;
      let pickupDate = formData.pickup_date;
      
      if (pickupTime === 'NOW') {
        const now = new Date();
        pickupTime = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
      }
      
      // Check if time is early morning (might be for tomorrow)
      const timeHour = parseInt(pickupTime.split(':')[0]);
      const isToday = formData.pickup_date.toDateString() === new Date().toDateString();
      const currentHour = new Date().getHours();
      
      // If today is selected and time is early morning but current time is late night, adjust to tomorrow
      if (isToday && timeHour < 6 && currentHour >= 22) {
        const tomorrow = new Date(pickupDate);
        tomorrow.setDate(tomorrow.getDate() + 1);
        pickupDate = tomorrow;
      }

      const payload = {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        pickup_address: formData.pickup_address,
        dropoff_address: formData.dropoff_address,
        fleet_type: formData.fleet_type,
        passengers: parseInt(formData.passengers) || 1,
        pickup_date: pickupDate ? format(pickupDate, 'yyyy-MM-dd') : '',
        pickup_time: pickupTime,
        driver_note: formData.driver_note,
      };

      // Use retry with exponential backoff for server errors
      await retryWithBackoff(() => axios.post(`${API}/bookings`, payload));
      setSubmitSuccess(true);
      
      // Track conversion for Google Ads
      trackBookingConversion();
      
      // Reset form after success
      setTimeout(() => {
        setFormData({
          full_name: '',
          email: '',
          phone: '',
          pickup_address: '',
          dropoff_address: '',
          fleet_type: '',
          passengers: '',
          pickup_date: null,
          pickup_time: '',
          driver_note: '',
        });
        setSubmitSuccess(false);
      }, 5000);
    } catch (err) {
      // More user-friendly error messages
      if (err.code === 'ERR_NETWORK' || !err.response) {
        setError('Connection error. Please check your internet and try again.');
      } else if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else if (err.response?.status >= 500) {
        setError('Server is temporarily unavailable. Please try again in a few moments.');
      } else {
        setError(err.response?.data?.detail || 'Failed to submit booking. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <div className="bg-white rounded-xl shadow-2xl border-t-4 border-primary p-8 md:p-10 text-center animate-fade-in-up">
        <div className="flex justify-center mb-4">
          <CheckCircle2 className="w-16 h-16 text-green-500" />
        </div>
        <h3 className="font-heading text-3xl font-bold text-secondary mb-3">Booking Received!</h3>
        <p className="text-muted-foreground mb-2">Thank you for choosing 13cabsbook.</p>
        <p className="text-muted-foreground">We will confirm your booking shortly via phone or email.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-2xl border-t-4 border-primary p-6 md:p-8">
      <h2 className="font-heading text-2xl md:text-3xl font-bold text-secondary mb-6 tracking-tight">
        Book Your Ride
      </h2>
      
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Full Name */}
        <div className="space-y-2">
          <Label htmlFor="full_name" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Full Name
          </Label>
          <Input
            id="full_name"
            name="full_name"
            data-testid="full-name-input"
            placeholder="John Smith"
            value={formData.full_name}
            onChange={handleInputChange}
            required
            className="h-11"
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            Phone
          </Label>
          <Input
            id="phone"
            name="phone"
            data-testid="phone-input"
            type="tel"
            placeholder="0412 345 678"
            value={formData.phone}
            onChange={handleInputChange}
            required
            className="h-11"
          />
        </div>
      </div>

      {/* Email */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="email" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Email Address
        </Label>
        <Input
          id="email"
          name="email"
          data-testid="email-input"
          type="email"
          placeholder="john@example.com"
          value={formData.email}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      {/* Pickup Address */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="pickup_address" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          <MapPin className="inline w-3 h-3 mr-1" /> Pick-up Address
        </Label>
        <Input
          ref={pickupInputRef}
          id="pickup_address"
          name="pickup_address"
          data-testid="pickup-input"
          placeholder="Enter pickup location in Victoria"
          value={formData.pickup_address}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      {/* Dropoff Address */}
      <div className="space-y-2 mb-4">
        <Label htmlFor="dropoff_address" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          <MapPin className="inline w-3 h-3 mr-1" /> Drop-off Address
        </Label>
        <Input
          ref={dropoffInputRef}
          id="dropoff_address"
          name="dropoff_address"
          data-testid="dropoff-input"
          placeholder="Enter destination in Victoria"
          value={formData.dropoff_address}
          onChange={handleInputChange}
          required
          className="h-11"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Fleet Type */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            <Car className="inline w-3 h-3 mr-1" /> Choose Fleet
          </Label>
          <Select onValueChange={(value) => handleSelectChange('fleet_type', value)}>
            <SelectTrigger data-testid="fleet-select" className="h-11">
              <SelectValue placeholder="Select vehicle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              <SelectItem value="premium_sedan">Premium Sedan</SelectItem>
              <SelectItem value="premium_suv">Premium SUV</SelectItem>
              <SelectItem value="sedan_taxi">Sedan Taxi</SelectItem>
              <SelectItem value="suv_taxi">SUV Taxi</SelectItem>
              <SelectItem value="maxi_taxi">Maxi Taxi</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Number of Passengers */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            <Users className="inline w-3 h-3 mr-1" /> Passengers
          </Label>
          <Select onValueChange={(value) => handleSelectChange('passengers', value)}>
            <SelectTrigger data-testid="passengers-select" className="h-11">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map((num) => (
                <SelectItem key={num} value={num.toString()}>
                  {num} {num === 1 ? 'Passenger' : 'Passengers'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Date */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            <CalendarIcon className="inline w-3 h-3 mr-1" /> Date
          </Label>
          <Popover open={datePopoverOpen} onOpenChange={setDatePopoverOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                data-testid="date-picker"
                className={cn(
                  "w-full h-11 justify-start text-left font-normal",
                  !formData.pickup_date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {formData.pickup_date ? format(formData.pickup_date, "PPP") : "Choose Date"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={formData.pickup_date}
                onSelect={handleDateChange}
                disabled={(date) => {
                  const today = new Date();
                  today.setHours(0, 0, 0, 0);
                  return date < today;
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Time */}
        <div className="space-y-2">
          <Label className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
            <Clock className="inline w-3 h-3 mr-1" /> Time
          </Label>
          <Select 
            value={formData.pickup_time} 
            onValueChange={(value) => handleSelectChange('pickup_time', value)}
          >
            <SelectTrigger ref={timeSelectRef} data-testid="time-select" className="h-11">
              <SelectValue placeholder="Select time" />
            </SelectTrigger>
            <SelectContent className="max-h-60">
              {generateTimeSlots().map((slot) => (
                <SelectItem key={slot.value} value={slot.value} className={slot.value === 'NOW' ? 'font-semibold text-primary' : ''}>
                  {slot.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Driver Note */}
      <div className="space-y-2 mb-6">
        <Label htmlFor="driver_note" className="text-xs font-bold uppercase tracking-widest text-muted-foreground">
          Driver Note (Optional)
        </Label>
        <Textarea
          id="driver_note"
          name="driver_note"
          data-testid="driver-note-input"
          placeholder="Any special instructions for the driver..."
          value={formData.driver_note}
          onChange={handleInputChange}
          rows={3}
          className="resize-none"
        />
      </div>

      <Button
        type="submit"
        data-testid="submit-booking"
        disabled={isSubmitting}
        className="w-full h-12 bg-primary hover:bg-primary-hover text-white font-bold text-lg tracking-wide rounded-lg transition-colors duration-200"
      >
        {isSubmitting ? 'Submitting...' : 'Book Now'}
      </Button>
    </form>
  );
};

export default BookingForm;
